import { useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon, cn } from "@nexus/ui";

export interface CollapsibleSectionProps {
  /** Uppercase mono section label (also used as the accessible name). */
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
  className?: string;
}

/**
 * Report-synthesis section wrapper: a mono uppercase heading that doubles as a
 * disclosure toggle, with the body animated open/closed via the grid-rows trick
 * (content stays mounted, so it remains accessible and searchable). Open by default.
 */
export function CollapsibleSection({
  title,
  children,
  defaultOpen = true,
  className,
}: CollapsibleSectionProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const headingId = useId();
  const bodyId = useId();

  return (
    <section
      className={cn("flex flex-col gap-3", className)}
      aria-labelledby={headingId}
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls={bodyId}
        aria-label={t(
          open ? "preflight.section.collapse" : "preflight.section.expand",
          {
            title,
          },
        )}
        className="group focus-visible:ring-ring/40 -mx-1 flex w-full items-center justify-between gap-2 rounded-sm px-1 py-0.5 outline-none focus-visible:ring-[3px]"
      >
        <span
          id={headingId}
          role="heading"
          aria-level={2}
          className="text-muted-foreground group-hover:text-foreground font-mono text-[11px] font-medium tracking-wide uppercase transition-colors"
        >
          {title}
        </span>
        <Icon
          name="chevron-down"
          size="sm"
          aria-hidden
          className={cn(
            "text-muted-foreground/60 group-hover:text-foreground shrink-0 transition-transform duration-200 motion-reduce:transition-none",
            open ? "" : "-rotate-90",
          )}
        />
      </button>
      <div
        id={bodyId}
        className={cn(
          "grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">{children}</div>
      </div>
    </section>
  );
}
